import { Component, OnInit, OnDestroy } from "@angular/core";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { SubSink } from "subsink";
import { BaseCommonService } from "@apps/patient-core/src/app/services/abstracts/common.service";
import { NhsPrService } from "@apps/patient-core/src/app/services/nhs-pr.service";
import { NavigationService } from "@apps/patient-core/src/app/services/navigation.service";
import { AppointmentEntry, CancellationInfoEntry } from "@apps/patient-core/src/app/data_model/appointment";
import { OverlayService } from "@apps/patient-core/src/app/services/overlay.service";
import { PracticeTimePipe } from "@dentally/ui/angular/src/pipes/practice-time.pipe";
import { LoadingComponent } from "@apps/patient-core/src/app/components/loading/loading.component";
import { ModalComponent } from "@apps/patient-core/src/app/components/modal/modal.component";
import { SHARED } from "@apps/patient-core/src/app/shared";
import { lastValueFrom } from "rxjs";
import { E_Appointments_When } from "@backend/graph/appointments/appointment-base";
import { NgIconComponent, provideIcons } from "@ng-icons/core";
import { heroExclamationCircleSolid } from "@ng-icons/heroicons/solid";
import { BookableApptsService } from "@apps/patient-app/src/app/services/bookable-appts.service";
import { AppointmentsService } from "@apps/patient-app/src/app/services/appointments.service";

dayjs.extend(utc);

export interface CancelDialogData {
  appointment?: AppointmentEntry;
}

@Component({
  selector: "dentr-modal-cancel-appointment",
  templateUrl: "./modal-cancel-appointment.component.html",
  standalone: true,
  imports: [PracticeTimePipe, LoadingComponent, ModalComponent, NgIconComponent, SHARED],
  providers: [provideIcons({ heroExclamationCircleSolid })],
})
export class ModalCancelAppointmentComponent implements OnInit, OnDestroy {
  public selectedAppointment: AppointmentEntry;
  public cancellation_info: CancellationInfoEntry | null = null;
  public showCard: "DEFAULT" | "LOADING" | "CANCELLING_DONE" | "ERROR" = "LOADING";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public multiBookingOnSameDayWarning: any;
  public sitePhoneNumber: string;
  private _subs = new SubSink();
  constructor(
    public commonService: BaseCommonService,
    private _appointmentsService: AppointmentsService,
    private _navigationService: NavigationService,
    private _nhsPrService: NhsPrService,
    private _overlayService: OverlayService,
    public bookableApptsService: BookableApptsService
  ) {
    const { data } = this._overlayService;
    if (!data || !data.appointment || !data.appointment.id || !data.appointment.start_time) {
      alert("Error loading appointment");
    } else {
      this.selectedAppointment = data.appointment;
      this.cancellation_info = this.selectedAppointment.cancellation_info;
      this.setup();
    }
  }
  ngOnDestroy() {
    this._subs.unsubscribe();
  }

  setup(): void {
    this.sitePhoneNumber = this.selectedAppointment.practitioner.site_phone_number;
    this.showCard = "LOADING";

    // Fallback to practice phone number if the sites phone number is not available
    if (!this.sitePhoneNumber) this.sitePhoneNumber = this.commonService.practice.phone_number;

    this._subs.sink = this._appointmentsService.onAppointmentsChanged.subscribe((event) => {
      if (!event?.when || !event.appointments || event.when === E_Appointments_When.PAST) return;
      if (event.appointments.length) {
        if (event.appointments && this.selectedAppointment) {
          const cApt = dayjs(this.selectedAppointment.start_time);
          event.appointments.forEach((appointment) => {
            if (appointment.id?.toString() !== this.selectedAppointment.id?.toString()) {
              const isSame = cApt.isSame(dayjs(appointment.start_time), "day");
              if (isSame) {
                this.multiBookingOnSameDayWarning = appointment;
              }
            }
          });
        }

        this.cancellation_info = this.selectedAppointment.cancellation_info;
        if (this.cancellation_info && !this.cancellation_info.cancellation_screen) {
          this.cancellation_info.cancellation_screen = "CONTACT_TO_CANCEL";
        }
      }
    });
  }

  ngOnInit() {
    this.showCard = "DEFAULT";
  }

  public async cancelAppointment(): Promise<void> {
    this.showCard = "LOADING";

    const res = await lastValueFrom(this._appointmentsService.cancelAppointment(this.selectedAppointment));

    if (res) {
      this.showCard = "CANCELLING_DONE";

      await this._appointmentsService.getAppointments(E_Appointments_When.FUTURE, true);
      this._nhsPrService.getPrData(true);
    } else {
      this.showCard = "ERROR";
    }
  }

  /**
   * Closes the modal and redirects the user to book appointment page
   */
  public rebookAppointment(): void {
    this._overlayService.close();

    this._navigationService.navigateToBooking();
  }

  public isAfter(ds: string) {
    return dayjs.utc(ds).isAfter(dayjs.utc());
  }

  public isBefore(ds: string) {
    return dayjs.utc(ds).isBefore(dayjs.utc());
  }

  close(): void {
    this._overlayService.close();
  }

  tryAgain(): void {
    this.setup();
  }
}
